"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.unfilledTextPlaceholders = exports.mapMailingAddressPlaceholders = exports.addressTagForName = exports.rewireServiceLinks = exports.readAnchors = exports.attrSpan = exports.mapText = exports.markupSpans = exports.tagEnds = exports.tagEnd = exports.classifyServiceLink = exports.REFUSE_TRACKING_BY_SEGMENT = exports.REFUSE_TRACKING_BY_NAME = exports.REFUSE_TRACKING_BY_LABEL = exports.isDeadHref = exports.endpointKinds = exports.labelKind = exports.normaliseLabel = exports.kindFromName = exports.placeholderName = exports.ENCHARGE_URL_TAG = exports.SERVICE_LINK_TAGS = void 0;
/**
 * The links a marketing email carries for its READER rather than for the
 * sender: unsubscribe, manage preferences and view in browser, and the one that
 * refuses email tracking (the stop page).
 *
 * An imported email arrives with another platform's version of each one: a
 * placeholder the old platform filled at send time (*|UNSUB|*,
 * [UNSUBSCRIBE_URL], {{ unsubscribe_url }}, <%asm_group_unsubscribe_raw_url%>)
 * or a live URL that belongs to the old platform. Encharge fills neither, so
 * left alone the reader gets a link that goes nowhere. An unsubscribe link
 * that goes nowhere is a legal problem, not a cosmetic one.
 *
 * This module decides which links are service links and what Encharge tag
 * each one becomes. It is shared by every place that has to agree on that:
 * the import's preparation step (service_layer/email_merge_tags), the engine
 * (html_to_unlayer), the engine's fidelity grade (html_to_unlayer_assess) and
 * the CAN-SPAM check (email_can_spam_compliance_helper). It has no
 * dependencies beyond an entity decoder, so the engine can be compiled with it
 * on its own.
 *
 * The rules are deliberately TIGHT. Matching loosely rewired "View our online
 * catalog" to the browser view, "Opt out of paper billing" to an email
 * unsubscribe, and a blog post whose URL contained "unsubscribe" to the
 * unsubscribe page, each time throwing away a real destination. So:
 *   - an href that IS a placeholder is classified by the placeholder's own
 *     name (any label);
 *   - an href that goes nowhere (empty, "#", a relative path, or a URL still
 *     carrying another platform's merge syntax) is classified by its label,
 *     and only a label that as a whole says "unsubscribe", "manage
 *     preferences" or "view in browser" counts;
 *   - a link whose label as a whole says "unsubscribe" or "manage
 *     preferences" is rewired whatever its href, since only Encharge's own
 *     pages can honour either;
 *   - any other live URL is rewired only when its path is an unsubscribe,
 *     preferences or web-version endpoint AND its label says the same thing
 *     (or it has no label at all, as an image link does);
 *   - the link that refuses email tracking (the stop page) follows the same
 *     rules: a placeholder named for it is the stop page, ahead of the
 *     placeholder's other meanings; a live URL is the stop page only when a
 *     whole path segment or host label names it; its words decide only on an
 *     href that goes nowhere.
 */
const html_entities_1 = require("html-entities");
const html_scan_1 = require("./html_scan");
/**
 * The Encharge tag each kind is wired to. Every one is URL-valued, so it
 * belongs INSIDE an href: entities/service.ts sets person.unsubscribeURL,
 * person.unsubscribeAllURL and person.managePreferencesURL to bare URLs, and
 * person.viewInBrowserURL to the placeholder the send path then swaps for
 * the browser-view URL. These are the four hrefs the editor's own "special
 * links" offer (EmailEditorUnlayerView).
 *
 * The `*Link` spellings (person.unsubscribeLink and friends) are different:
 * they render a whole `<a ...>Unsubscribe</a>` element. Written into an href,
 * the attribute ends at the element's first quote, the real URL never becomes
 * a link, and `">Unsubscribe</a>">` leaks into the visible text.
 */
exports.SERVICE_LINK_TAGS = {
    unsubscribe: "{{person.unsubscribeURL}}",
    unsubscribeAll: "{{person.unsubscribeAllURL}}",
    preferences: "{{person.managePreferencesURL}}",
    viewInBrowser: "{{person.viewInBrowserURL}}",
    // the stop page of email tracking consent: opening it changes nothing, its
    // button refuses (entities/service.ts sets person.stopTrackingURL)
    stopTracking: "{{person.stopTrackingURL}}",
};
/** Encharge's URL-valued service tags, however they are spaced. */
exports.ENCHARGE_URL_TAG = /^\{\{\s*person\.(unsubscribeURL|unsubscribeAllURL|managePreferencesURL|viewInBrowserURL|viewInBrowserUrl|stopTrackingURL)\s*\}\}$/;
const ENCHARGE_TAG_KIND = {
    unsubscribeURL: "unsubscribe",
    unsubscribeAllURL: "unsubscribeAll",
    managePreferencesURL: "preferences",
    viewInBrowserURL: "viewInBrowser",
    viewInBrowserUrl: "viewInBrowser",
    stopTrackingURL: "stopTracking",
};
// ---------------------------------------------------------------------------
// Placeholders: an href that is, as a whole, another platform's merge tag.
/**
 * The delimiters platforms and hand-coders wrap a placeholder in. Each
 * pattern must match the WHOLE href, and captures what stands inside, blank
 * space and all: placeholderName trims it and takes it only when 1 to 80
 * characters are left (MAX_PLACEHOLDER_NAME). The blank space is not read by
 * the pattern: /^\*\|\s*([^|]{1,80}?)\s*\|\*$/ tried every split of a run of
 * blank space between the two \s* and the name, which grew as its cube (4 KB
 * of tabs in one href: 1 s).
 */
const PLACEHOLDER_FORMS = [
    // Mailchimp *|UNSUB|*, *|ARCHIVE|*, *|UPDATE_PROFILE|*
    /^\*\|([^|]*)\|\*$/,
    // Braze {{${unsubscribe_url}}}
    /^\{\{\s*\$\{([^{}]*)\}\s*\}\}$/,
    // {{unsubscribe_url}}, {{ unsubscribe }}, SendGrid {{{unsubscribe}}}
    /^\{\{\{?([^{}]*)\}?\}\}$/,
    // Klaviyo / Customer.io {% unsubscribe_url %} (a "-" of whitespace control at either end left off after)
    /^\{%-?([^%]*)%\}$/,
    // SendGrid <%asm_group_unsubscribe_raw_url%>
    /^<%=?([^%<>]*)%>$/,
    // Marketing Cloud / Stripo %%unsubscribe%%, %%[unsub_center_url]%%
    /^%%\[?([^%\]]*)\]?%%$/,
    // Mailgun %unsubscribe_url%, ActiveCampaign %UNSUBSCRIBELINK%
    /^%\s*([A-Za-z_][\w.:-]{0,79}?)\s*%$/,
    // ${unsubscribe_url}
    /^\$\{([^{}]*)\}$/,
    // MailerLite {$unsubscribe}
    /^\{\$([^{}]*)\}$/,
    // AWeber {!remove_web}
    /^\{!([^{}]*)\}$/,
    // $unsubscribe$
    /^\$\s*([A-Za-z_][\w.-]{0,79}?)\s*\$$/,
    // Mailjet [[UNSUB_LINK_EN]], GetResponse [[remove]]
    /^\[\[([^[\]]*)\]\]$/,
    // [UNSUBSCRIBE_URL], SendGrid [Unsubscribe], Sendy [unsubscribe]
    /^\[([^[\]]*)\]$/,
    // {unsubscribe}
    /^\{\s*([A-Za-z_][\w.:-]{0,79}?)\s*\}$/,
    // Moosend #unsubscribeLink#
    /^#\s*([A-Za-z_][\w.-]{0,79}?)\s*#$/,
];
/** The most characters a placeholder's name takes, its blank space round it not counted. */
const MAX_PLACEHOLDER_NAME = 80;
/** An href the way it may have been written: entity- or percent-encoded. */
const hrefVariants = (href) => {
    const raw = String(href || "").trim();
    const out = [raw];
    const decoded = (0, html_entities_1.decode)(raw).trim();
    if (decoded !== raw)
        out.push(decoded);
    if (/%(?:7B|7D|7C|5B|5D|24|23|25|2A|3C|3E)/i.test(decoded)) {
        try {
            out.push(decodeURIComponent(decoded).trim());
        }
        catch (_a) {
            // not percent-encoding after all (%unsubscribe_url% is a placeholder)
        }
    }
    return out;
};
/**
 * The name inside a placeholder href, or undefined when the href is not one.
 * `{{ unsubscribe_url | default: '' }}` names `unsubscribe_url`.
 */
const placeholderName = (href) => {
    for (const variant of hrefVariants(href)) {
        for (const form of PLACEHOLDER_FORMS) {
            const m = form.exec(variant);
            if (!m)
                continue;
            // what stands inside, its blank space and a Liquid tag's whitespace control ("-") left off
            let inner = m[1].trim();
            if (inner.endsWith("-") && variant.startsWith("{%"))
                inner = inner.slice(0, -1).trim();
            if (!inner || inner.length > MAX_PLACEHOLDER_NAME)
                continue;
            const name = inner.split(/[|(,\s]/)[0].trim();
            if (name)
                return name;
        }
    }
    return undefined;
};
exports.placeholderName = placeholderName;
/** The words a placeholder name is made of: UNSUBSCRIBE_URL, unsubscribeLink. */
const nameWords = (name) => name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
/**
 * What a placeholder's name says it is for. Only names that plainly carry
 * the meaning count: `[LINK]` or `{{url}}` say nothing, and their label
 * decides instead.
 */
const kindFromName = (name) => {
    const words = nameWords(name);
    const compact = words.join("");
    // SendGrid's {{{unsubscribe_preferences}}} is its preference page, not
    // its unsubscribe: a name that says preferences is that, whatever else
    if (words.some((w) => /^(?:preferences?|prefs)$/.test(w)) && !words.includes("all"))
        return "preferences";
    if (/unsub|optout/.test(compact) || words[0] === "remove") {
        if (words.includes("all") ||
            words.includes("global") ||
            /unsubscribeall|allunsub|globalunsub/.test(compact))
            return "unsubscribeAll";
        return "unsubscribe";
    }
    // "profile" only as the old platform's profile PAGE: {{ author_profile_url }}
    // in a digest is somebody's profile, not the reader's preferences
    if (/preference|prefs|updateprofile|manageprofile|profilecent|managesubscription|subscriptioncent|subscriptionmanage|updatelink/.test(compact))
        return "preferences";
    // "archive", "mirror" and "permalink" only as the whole name: an RSS
    // template's {{ item.permalink }} is the article, not this email online
    if (/browser|webversion|webview|viewonline|onlineversion|viewaspage|viewemail|webcopy/.test(compact) ||
        /^(?:archive(?:link|url)?(?:short)?|mirror|permalink)$/.test(compact))
        return "viewInBrowser";
    return undefined;
};
exports.kindFromName = kindFromName;
// ---------------------------------------------------------------------------
// Labels: what the reader is told the link does.
/** A label as one lower-case line, without the punctuation around it. */
/** The marks a link's words are trimmed of at either end (normaliseLabel). */
const LABEL_TRIM = /[\s.,;:!?|*"'()[\]\u00b7\u2022\u00bb\u00ab\u203a\u2039\u2192\u2190>\u2013\u2014-]/;
/**
 * `text` without the LABEL_TRIM marks at either end, read one character at a
 * time from each end: the pattern /[marks]+$/g it replaces started again at
 * every mark of a long run in the middle, and read the run to its end each
 * time (quadratic).
 */
const trimLabelMarks = (text) => {
    let start = 0;
    let end = text.length;
    while (start < end && LABEL_TRIM.test(text[start]))
        start += 1;
    while (end > start && LABEL_TRIM.test(text[end - 1]))
        end -= 1;
    return text.slice(start, end);
};
const normaliseLabel = (label) => trimLabelMarks((0, html_entities_1.decode)(String(label || ""))
    .replace(/[\u00a0\u200b-\u200d\u2060\ufeff]/g, " ")
    .replace(/[\u2018\u2019]/g, "'")
    .toLowerCase()
    .replace(/\s+/g, " "));
exports.normaliseLabel = normaliseLabel;
// "Don't want these emails? Unsubscribe": one question may lead the call to action
const LEAD = "(?:[^?]{0,60}\\? )?(?:please |you can |click here to |click to |tap here to )?";
const SOME = "(?:(?:all|any|these|this|our|your|future|further|marketing|promotional|such|the) )*";
const EMAILS = "(?:e-?mails?|mailings?|messages|newsletters?|communications?|updates|promotions|(?:mailing )?list)";
const VIEW_WHERE = "(?:online|in (?:a |your |the )?(?:web ?)?browser|as (?:a )?web ?page|on (?:the )?web)";
const WHAT = "(?:(?:email|e-mail|message|newsletter|issue|version|post|mail) )?";
const PREF_QUAL = "(?:(?:email|e-mail|subscription|communication|mailing|newsletter|contact|marketing|list) )";
const LABELS = [
    [
        "unsubscribeAll",
        new RegExp(`^${LEAD}unsubscribe from all(?: (?:of )?(?:our |these |future )?${EMAILS})?(?: from .{1,40})?$`),
    ],
    [
        "unsubscribe",
        new RegExp(`^${LEAD}(?:` +
            [
                "(?:unsubscribe|unsub)(?: (?:here|now|instantly|below|at any time|anytime))?(?: from (?!(?:our |the |your |all )?(?:sms|text(?:s| messages?)|push|whatsapp|this (?:thread|conversation|discussion|post|topic))\\b).{1,60})?",
                "opt[ -]?out(?: (?:here|now))?",
                `opt[ -]?out (?:of|from) ${SOME}${EMAILS}`,
                `(?:stop|cancel) (?:receiving |getting )?${SOME}${EMAILS}`,
                `remove me(?: from ${SOME}${EMAILS})?`,
                `(?:i )?(?:no longer|don't|do not) want (?:to receive |to get )?${SOME}${EMAILS}`,
                `leave ${SOME}${EMAILS}`,
            ].join("|") +
            ")$"),
    ],
    [
        "preferences",
        new RegExp(`^${LEAD}(?:` +
            [
                `(?:update|manage|change|edit|set|review|adjust|customi[sz]e|view|choose) (?:your |my )?${PREF_QUAL}*(?:preferences?|prefs)`,
                `(?:your |my )?${PREF_QUAL}+(?:preferences?|prefs|settings)`,
                "(?:(?:email|subscription|communication) )?preference cent(?:er|re)",
                "(?:update|manage|change|edit) (?:your |my )?(?:email|e-mail|mailing|newsletter) subscriptions?",
                "(?:update|manage|change|choose) (?:what|which|how many|how often)(?: e-?mails?)? (?:you )?(?:receive|get)(?: from us)?",
            ].join("|") +
            ")$"),
    ],
    [
        "viewInBrowser",
        new RegExp(`^${LEAD}(?:` +
            [
                `view (?:it |this |the )?${WHAT}${VIEW_WHERE}`,
                `(?:open|read|see) (?:it |this |the )?${WHAT}${VIEW_WHERE}`,
                "(?:view (?:the )?)?(?:web|online|browser) version(?: of (?:this|the) (?:email|message|newsletter))?",
                "web ?view",
                "(?:having )?trouble (?:viewing|reading|seeing)(?: (?:this|it))?(?: (?:email|e-mail|message))?",
                "(?:(?:this )?email )?not displaying (?:correctly|properly)",
                "(?:can't|cannot) see (?:this email|the images|images)(?: (?:properly|correctly))?",
            ].join("|") +
            ")$"),
    ],
];
/** The service a label as a whole asks for, or undefined. */
const labelKind = (label) => {
    const text = (0, exports.normaliseLabel)(label);
    if (!text || text.length > 140)
        return undefined;
    for (const [kind, re] of LABELS)
        if (re.test(text))
            return kind;
    return undefined;
};
exports.labelKind = labelKind;
// ---------------------------------------------------------------------------
// Live URLs: which ones are another platform's service endpoint.
const URL_PARTS = /^(?:https?:)?\/\/([^/?#\s]+)([^?#\s]*)(\?[^#\s]*)?/i;
const EXT = "(?:\\.(?:php|html?|aspx?|jsp|cgi))?";
const ENDPOINT_SEGMENTS = [
    [
        "unsubscribe",
        new RegExp(`^(?:unsub|unsubscribe|opt[-_]?out|unsubscribe[-_]?all|unsub[-_]?cent(?:er|re)|unsubscribe[-_]?(?:cent(?:er|re)|confirm|page))${EXT}$`),
    ],
    [
        "preferences",
        new RegExp(`^(?:preferences?|prefs|preference[-_]?cent(?:er|re)|manage[-_]?preferences|email[-_]?preferences|subscription[-_]?(?:preferences|cent(?:er|re)|management|settings)|manage[-_]?subscriptions?|update[-_]?profile)${EXT}$`),
    ],
    [
        "viewInBrowser",
        new RegExp(`^(?:web[-_]?version|webview|web[-_]?view|view[-_]?in[-_]?browser|view[-_]?online|browser[-_]?view|email[-_]?browser[-_]?view)${EXT}$`),
    ],
];
/**
 * The service endpoints a live URL's path names, in priority order. A path
 * SEGMENT has to be the endpoint's name: /blog/why-readers-unsubscribe is an
 * article, /unsub?u=1 is an endpoint.
 */
const endpointKinds = (href) => {
    const m = URL_PARTS.exec(String(href || "").trim());
    if (!m)
        return [];
    const host = m[1].toLowerCase();
    const segments = m[2]
        .split("/")
        .map((s) => {
        try {
            return decodeURIComponent(s).toLowerCase();
        }
        catch (_a) {
            return s.toLowerCase();
        }
    })
        .filter(Boolean);
    const query = m[3] || "";
    const found = new Set();
    for (const [kind, re] of ENDPOINT_SEGMENTS)
        if (segments.some((s) => re.test(s)))
            found.add(kind);
    if (/^(?:unsub|unsubscribe|optout)\./.test(host))
        found.add("unsubscribe");
    if (/(?:^|[?&])(?:act|action|a)=unsub(?:scribe)?(?:&|$)/i.test(query))
        found.add("unsubscribe");
    if (/(?:^|\.)campaign-archive\d*\.com$|(?:^|\.)mailchi\.mp$/.test(host))
        found.add("viewInBrowser");
    return ["unsubscribe", "preferences", "viewInBrowser"].filter((k) => found.has(k));
};
exports.endpointKinds = endpointKinds;
/** Another platform's merge syntax somewhere inside an href. */
const FOREIGN_MERGE = /\*\||\{\{(?!\s*(?:person|account)\.)|\{%|%%|\[\[|<%|&lt;%|\$\{|\{\$|\[[A-Z][A-Z0-9_]{2,}\]/;
/**
 * An href that is not a working destination: empty, "#", javascript:, a
 * relative path (an email has no base URL), or a URL still carrying another
 * platform's merge syntax. Only then may the label decide.
 */
const isDeadHref = (href) => {
    const h = String(href || "").trim();
    if (!h || /^#/.test(h) || /^(?:javascript:|about:blank)/i.test(h))
        return true;
    if (FOREIGN_MERGE.test(h))
        return true;
    return !/^(?:https?:|mailto:|tel:|sms:|\/\/|\{\{)/i.test(h);
};
exports.isDeadHref = isDeadHref;
// ---------------------------------------------------------------------------
// The link that refuses email tracking
/**
 * The link that refuses email tracking (Brevo's "Opt out of the use of tracking
 * pixels", Sarbacane's "refuse the tracking pixel", Customer.io's tracking
 * consent URL) is not an unsubscribe. The unsubscribe rules match its "opt out"
 * wording and its "unsub" or "optout" href, so before this rule it became
 * {{person.unsubscribeURL}}: a reader who only wanted no tracking left the
 * whole list. Encharge has a tag of its own for it, the stop page (rule F10).
 *
 * By label: a tracking word (tracking, pixel, suivi, tracciamento) beside a
 * word that says no (opt out, refuse, withdraw, stop, "ne pas", rifiuta) or a
 * choice about it (tracking preferences). "Track your order" and "Order
 * tracking" say neither, so they stay what they are. Like every label rule it
 * applies only to an href that is not a destination (isDeadHref).
 */
const SAYS_NO = "(?:opt[\\s_-]?out|refus|revok|revoc|withdraw|disabl|decline|turn off|prefer not|rifiut|disattiv|non (?:voglio|desidero)|stop(?:\\s|$))";
const TRACKING_WORD = "(?:tracking|pixels?|suivi|tracciamento|tracciat[oi])";
// "Je prefere ne pas etre suivi" says both at once
const SAYS_NO_TRACKING_IN_FRENCH = "ne (?:pas|plus) (?:[eê]tre|me) suivi|ne (?:pas|plus) me suivre|ne pas suivre";
exports.REFUSE_TRACKING_BY_LABEL = new RegExp(`${SAYS_NO}[\\s\\S]{0,60}${TRACKING_WORD}|${TRACKING_WORD}[\\s\\S]{0,60}${SAYS_NO}|tracking\\s+(?:preferences|consent|choices|settings)|${SAYS_NO_TRACKING_IN_FRENCH}`, "i");
/**
 * By a placeholder's NAME, which is the other platform's own word for the link
 * (Brevo's revoke_open_pixel_tracking, Customer.io's tracking_consent_url,
 * OPTOUT_PIXEL). It is read before the placeholder's other meanings
 * (kindFromName), which the "optout" or "unsub" in the same name would take.
 */
exports.REFUSE_TRACKING_BY_NAME = /revoke_open_pixel_tracking|tracking_consent_url|(?:opt[_-]?out|unsub(?:scribe)?|revoke|refuse|withdraw|decline|disable|stop)[a-z_-]{0,16}(?:pixel|track)|(?:pixel|tracking)[_-]?(?:consent|opt[_-]?out|preferences?)/i;
/**
 * By a live address, which has to say it AS A WHOLE, the way every endpoint of
 * the other service links does (endpointKinds): one path segment, the first
 * label of the host, or the value of an act, action or a parameter IS the
 * refuse endpoint's name: optout-tracking, unsub-tracking, revoke-pixel,
 * unsubscribe-pixel-tracking, tracking-consent. A word of it sitting inside a
 * longer address is an article or a product (/blog/how-to-disable-tracking-on-
 * your-iphone, /products/stopwatch-tracker, /blog/opt-out-of-tracking-cookies)
 * and stays where it points. "track" alone is not enough either:
 * /unsubscribe-track/123 may well be a tracked unsubscribe address.
 */
const NO_VERB = "(?:opt[-_]?out|unsub(?:scribe)?|revoke|refuse|withdraw|decline|disable|stop)";
const NO_FILLER = "(?:(?:the|my|all|email|emails|open|opens|click|clicks|link|links)[-_]?)*";
const ENDPOINT_TAIL = "(?:[-_]?(?:pixels?|tracking|url|link|page))*";
exports.REFUSE_TRACKING_BY_SEGMENT = new RegExp(`^(?:${NO_VERB}[-_]?${NO_FILLER}(?:pixels?|tracking)|(?:pixel|tracking)[-_]?(?:consent|opt[-_]?out))${ENDPOINT_TAIL}${EXT}$`);
/** Does a live address name the refuse endpoint as a whole (see REFUSE_TRACKING_BY_SEGMENT)? */
const addressNamesRefuseTracking = (href) => {
    const h = String(href || "").trim();
    const named = (piece) => {
        let text = piece;
        try {
            text = decodeURIComponent(piece);
        }
        catch (_a) {
            // not percent-encoding after all
        }
        return text.length <= 80 && exports.REFUSE_TRACKING_BY_SEGMENT.test(text.toLowerCase());
    };
    let path;
    let query = "";
    const m = URL_PARTS.exec(h);
    if (m) {
        if (named(m[1].split(".")[0]))
            return true;
        path = m[2];
        query = m[3] || "";
    }
    else {
        const end = h.search(/[?#]/);
        path = end === -1 ? h : h.slice(0, end);
        if (end !== -1 && h[end] === "?")
            query = h.slice(end).split("#")[0];
    }
    if (path.split("/").some(named))
        return true;
    return query
        .slice(1)
        .split("&")
        .some((pair) => {
        const eq = pair.indexOf("=");
        return eq > 0 && /^(?:act|action|a)$/i.test(pair.slice(0, eq)) && named(pair.slice(eq + 1));
    });
};
/** Do the link's words, as a whole, refuse email tracking? Only an href that is no destination lets them decide. */
const labelRefusesTracking = (label) => {
    const text = (0, exports.normaliseLabel)(label);
    return Boolean(text) && text.length <= 140 && exports.REFUSE_TRACKING_BY_LABEL.test(text);
};
const match = (kind, by, already = false) => ({ kind, tag: exports.SERVICE_LINK_TAGS[kind], already, by });
/**
 * Is this link a service link, and which Encharge tag should its href be?
 * `label` is the link's visible text (or its image's alt text).
 */
const classifyServiceLink = (href, label) => {
    const h = String(href || "").trim();
    // Encharge's own tag, the editor's entity spaces in it too ({{&nbsp;person.unsubscribeURL}}), as the send decodes a
    // one-line tag: its own, never rewired
    const own = exports.ENCHARGE_URL_TAG.exec(h) || (h.includes("&") || /[\u00a0\u180e\ufeff\u2000-\u200a]/.test(h) ? exports.ENCHARGE_URL_TAG.exec((0, html_entities_1.decode)(h).replace(/[\s\u180e]+/g, " ")) : null);
    if (own)
        return match(ENCHARGE_TAG_KIND[own[1]], "encharge-tag", true);
    // A link that refuses email tracking is the stop page and never an
    // unsubscribe or a preferences link. In this order, so that the other
    // platform's own words for each link win over ours:
    //  1. a placeholder named for it (revoke_open_pixel_tracking);
    //  2. a placeholder named for anything else (*|UNSUB|* stays the unsubscribe
    //     whatever its label says about tracking);
    //  3. an address that names it as a whole;
    //  4. on an href that is no destination, its words, before the label rules
    //     below, whose "opt out" wording they would match.
    const name = (0, exports.placeholderName)(h);
    if (name && exports.REFUSE_TRACKING_BY_NAME.test(name))
        return match("stopTracking", "placeholder");
    if (name) {
        const kind = (0, exports.kindFromName)(name);
        if (kind)
            return match(kind, "placeholder");
    }
    if (addressNamesRefuseTracking(h))
        return match("stopTracking", "endpoint");
    if ((0, exports.isDeadHref)(h) && labelRefusesTracking(label))
        return match("stopTracking", "label");
    const fromLabel = (0, exports.labelKind)(label);
    if (name || (0, exports.isDeadHref)(h))
        return fromLabel ? match(fromLabel, "label") : undefined;
    // A link whose words as a whole ask to unsubscribe or to manage the
    // reader's preferences is that link, wherever it points: Encharge honours
    // only its own unsubscribe and preferences pages. A builder's templates
    // point "Unsubscribe" at the builder's home page (https://maileditor.net/),
    // and left there the import had no working unsubscribe. The label rules
    // are what keep "Opt out of paper billing" and "Why readers unsubscribe"
    // where they point. A "View in browser" link to a live page may be the
    // sender's own archive, so that one still needs the endpoint below.
    if (fromLabel && fromLabel !== "viewInBrowser")
        return match(fromLabel, "label");
    const endpoints = (0, exports.endpointKinds)(h);
    if (!endpoints.length)
        return undefined;
    if (fromLabel)
        return endpoints.includes(fromLabel) ? match(fromLabel, "endpoint") : undefined;
    // an image link says nothing; its endpoint is all there is to go on
    return (0, exports.normaliseLabel)(label) ? undefined : match(endpoints[0], "endpoint");
};
exports.classifyServiceLink = classifyServiceLink;
// ---------------------------------------------------------------------------
// Reading markup as written, without a parser repairing it.
const RAW_TEXT = /^(script|style|title|textarea|xmp)$/i;
/** Index of the `>` that ends the tag whose name ends at `from`, or -1. */
const tagEnd = (html, from) => {
    let i = from;
    const n = html.length;
    while (i < n) {
        const c = html[i];
        if (c === ">")
            return i;
        if (c === "=") {
            i += 1;
            while (i < n && /\s/.test(html[i]))
                i += 1;
            const q = html[i];
            if (q === '"' || q === "'") {
                const close = html.indexOf(q, i + 1);
                if (close === -1)
                    return -1;
                i = close + 1;
                continue;
            }
            continue;
        }
        i += 1;
    }
    return -1;
};
exports.tagEnd = tagEnd;
/**
 * tagEnd for one document, remembering every place a scan passed outside a
 * quoted value and where that scan ended: a later scan that reaches such a
 * place ends where it did. markupSpans scans from each `<` whose tag it
 * could not close, and a tail of unclosed quoted attributes (`<td
 * background="`) read to the end of the document once per `<` (15 s at
 * 400 KB, in the import's preparation step).
 */
const tagEnds = (html) => {
    // 0: not known; else the end + 2 (so -1 is 1)
    const memo = new Int32Array(html.length + 1);
    const n = html.length;
    // one step of tagEnd from an outer place: the next outer place, or a result
    const step = (i) => {
        const c = html[i];
        if (c === ">")
            return { result: i };
        if (c === "=") {
            i += 1;
            while (i < n && /\s/.test(html[i]))
                i += 1;
            const q = html[i];
            if (q === '"' || q === "'") {
                const close = html.indexOf(q, i + 1);
                if (close === -1)
                    return { result: -1 };
                return { next: close + 1 };
            }
            return { next: i };
        }
        return { next: i + 1 };
    };
    return (from) => {
        let result = -1;
        for (let i = from; i < n;) {
            if (memo[i]) {
                result = memo[i] - 2;
                break;
            }
            const s = step(i);
            if (s.result !== undefined) {
                result = s.result;
                break;
            }
            i = s.next;
        }
        // every outer place this scan passed ends where it did
        for (let i = from; i < n && !memo[i];) {
            memo[i] = result + 2;
            const s = step(i);
            if (s.result !== undefined)
                break;
            i = s.next;
        }
        return result;
    };
};
exports.tagEnds = tagEnds;
/**
 * The markup cut into text, tags, comments and raw-text element bodies
 * (script, style, title), in one linear pass. Anything that is not a tag is
 * text, so a stray "<" in copy stays copy.
 */
const markupSpans = (html) => {
    const spans = [];
    const n = html.length;
    const endOf = (0, exports.tagEnds)(html);
    let i = 0;
    let text = 0;
    const flushText = (to) => {
        if (to > text)
            spans.push({ start: text, end: to, kind: "text" });
    };
    while (i < n) {
        const lt = html.indexOf("<", i);
        if (lt === -1)
            break;
        if (html.startsWith("<!--", lt)) {
            const close = html.indexOf("-->", lt + 4);
            const end = close === -1 ? n : close + 3;
            flushText(lt);
            spans.push({ start: lt, end, kind: "comment" });
            i = text = end;
            continue;
        }
        if (html[lt + 1] === "!" || html[lt + 1] === "?") {
            // <!DOCTYPE ...>, <![endif]>, <?xml ...?>
            const close = html.indexOf(">", lt + 2);
            if (close === -1)
                break;
            flushText(lt);
            spans.push({ start: lt, end: close + 1, kind: "tag", name: html[lt + 1] });
            i = text = close + 1;
            continue;
        }
        const m = /^<(\/?)([A-Za-z][\w:-]*)/.exec(html.slice(lt, lt + 64));
        if (!m) {
            i = lt + 1;
            continue;
        }
        const close = endOf(lt + m[0].length);
        if (close === -1) {
            i = lt + 1;
            continue;
        }
        flushText(lt);
        const name = m[2].toLowerCase();
        spans.push({ start: lt, end: close + 1, kind: "tag", name, closing: m[1] === "/" });
        i = text = close + 1;
        if (!m[1] && RAW_TEXT.test(name)) {
            const re = new RegExp(`</${name}\\s*>`, "ig");
            re.lastIndex = i;
            const endTag = re.exec(html);
            const bodyEnd = endTag ? endTag.index : n;
            if (bodyEnd > i)
                spans.push({ start: i, end: bodyEnd, kind: "raw" });
            i = text = bodyEnd;
        }
    }
    flushText(n);
    return spans;
};
exports.markupSpans = markupSpans;
/** Rewrite only the TEXT between tags (not comments, scripts or attributes). */
const mapText = (html, fn) => {
    let out = "";
    for (const span of (0, exports.markupSpans)(html)) {
        const piece = html.slice(span.start, span.end);
        out += span.kind === "text" ? fn(piece) : piece;
    }
    return out;
};
exports.mapText = mapText;
/** An attribute's value and where it sits, read from one start tag. */
const attrSpan = (tag, attr) => {
    var _a, _b;
    const re = /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
    // skip the element name
    const head = /^<\s*[A-Za-z][\w:-]*/.exec(tag);
    re.lastIndex = head ? head[0].length : 0;
    let m;
    // eslint-disable-next-line no-cond-assign
    while ((m = re.exec(tag))) {
        if (m[1].toLowerCase() !== attr)
            continue;
        const value = (_b = (_a = m[2]) !== null && _a !== void 0 ? _a : m[3]) !== null && _b !== void 0 ? _b : m[4];
        if (value === undefined)
            return { value: "", start: -1, end: -1 };
        const at = m.index + m[0].length - value.length - (m[2] !== undefined || m[3] !== undefined ? 1 : 0);
        return { value, start: at, end: at + value.length };
    }
    return undefined;
};
exports.attrSpan = attrSpan;
const stripToText = (html) => (0, html_entities_1.decode)(html
    .replace(/<!--[\s\S]*?(?:-->|$)/g, " ")
    // (`.replace(/<[^>]*>/g, " ")`, see TAGS)
    .replace(html_scan_1.TAGS, html_scan_1.spaceTag))
    .replace(/[\u00a0\u200b-\u200d\u2060\ufeff]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
/**
 * Every `<a>` in the markup with its href and label, read as written. An
 * `<a>` is closed by its `</a>` or, as the HTML parser does it, by the next
 * `<a>`. Anchors inside comments are reported too (flagged), because an
 * Outlook-only link still needs a working destination.
 */
const readAnchors = (html) => {
    const out = [];
    const scan = (source, offset, inComment) => {
        const spans = (0, exports.markupSpans)(source);
        for (let k = 0; k < spans.length; k += 1) {
            const s = spans[k];
            if (s.kind === "comment") {
                // an Outlook conditional keeps real markup inside its comment
                const body = source.slice(s.start + 4, Math.max(s.start + 4, s.end - 3));
                if (/<a[\s>]/i.test(body))
                    scan(body, offset + s.start + 4, true);
                continue;
            }
            if (s.kind !== "tag" || s.name !== "a" || s.closing)
                continue;
            let contentEnd = source.length;
            for (let j = k + 1; j < spans.length; j += 1) {
                const t = spans[j];
                if (t.kind === "tag" && t.name === "a") {
                    contentEnd = t.start;
                    break;
                }
            }
            const tag = source.slice(s.start, s.end);
            const href = (0, exports.attrSpan)(tag, "href");
            const content = source.slice(s.end, contentEnd);
            const text = stripToText(content);
            // (read to the last `>`: see throughLast)
            const img = /<img\b[^>]*>/i.exec((0, html_scan_1.throughLast)(content.replace(/<!--[\s\S]*?(?:-->|$)/g, " ")));
            const alt = img ? ((0, exports.attrSpan)(img[0], "alt") || { value: "" }).value : "";
            out.push({
                start: offset + s.start,
                openEnd: offset + s.end,
                contentEnd: offset + contentEnd,
                href: href && href.start >= 0
                    ? { value: href.value, start: offset + s.start + href.start, end: offset + s.start + href.end }
                    : href
                        ? { value: "", start: -1, end: -1 }
                        : undefined,
                label: text || stripToText(alt),
                clickable: Boolean(text || img),
                inComment,
            });
        }
    };
    scan(html, 0, false);
    return out;
};
exports.readAnchors = readAnchors;
/**
 * Campaign Monitor writes its service links as their own elements:
 * <unsubscribe>Unsubscribe</unsubscribe>, <webversion>View online</webversion>,
 * <preferences>Manage preferences</preferences>.
 */
const CM_ELEMENTS = {
    unsubscribe: "unsubscribe",
    webversion: "viewInBrowser",
    preferences: "preferences",
};
/**
 * Point every service link in the markup at its Encharge tag. Only the href
 * value is replaced, byte for byte everything else stays as written, so a
 * template kept as classic HTML sends with working links too.
 */
const rewireServiceLinks = (html, 
/** a link the caller keeps exactly as written (Encharge's own Liquid, a tag of no platform's evidence): it names it */
keep) => {
    const seen = new Map();
    const note = (label, from, to) => {
        const key = `${label}\u0000${from}\u0000${to}`;
        const entry = seen.get(key) || { label, from, to, count: 0 };
        entry.count += 1;
        seen.set(key, entry);
    };
    let out = html.replace(/<(unsubscribe|webversion|preferences)(\s[^<>]*)?>([\s\S]{0,2000}?)<\/\1\s*>/gi, (_whole, name, attrs, inner) => {
        const tag = exports.SERVICE_LINK_TAGS[CM_ELEMENTS[name.toLowerCase()]];
        note(stripToText(inner), `<${name.toLowerCase()}>`, tag);
        return `<a href="${tag}"${attrs || ""}>${inner}</a>`;
    });
    const edits = [];
    for (const a of (0, exports.readAnchors)(out)) {
        if (!a.href || a.href.start < 0)
            continue;
        const found = (0, exports.classifyServiceLink)(a.href.value, a.label);
        if (!found || found.already)
            continue;
        if (keep && keep(a.href.value, a.label, found))
            continue;
        edits.push({ start: a.href.start, end: a.href.end, value: found.tag });
        note(a.label, a.href.value, found.tag);
    }
    if (edits.length) {
        let rebuilt = "";
        let at = 0;
        for (const e of edits.sort((x, y) => x.start - y.start)) {
            rebuilt += out.slice(at, e.start) + e.value;
            at = e.end;
        }
        out = rebuilt + out.slice(at);
    }
    return { html: out, rewired: Array.from(seen.values()) };
};
exports.rewireServiceLinks = rewireServiceLinks;
// ---------------------------------------------------------------------------
// The sender's postal address
/**
 * Placeholders that stand for the SENDER's postal address, which CAN-SPAM
 * requires in every marketing email. Encharge has one tag for it,
 * {{account.mailingAddress}} (and a one-line variant), filled from the
 * account settings at send time.
 *
 * A generic name needs a qualifier that makes it the sender's: MAILING,
 * PHYSICAL, POSTAL, COMPANY, BUSINESS, SENDER, LIST, ORGANIZATION... A bare
 * ADDRESS is the contact's own address field in most platforms (translated
 * elsewhere as {{person.address}}), and EMAIL_ADDRESS or SHIPPING_ADDRESS are
 * not a postal address of the sender at all.
 */
const ADDRESS_QUALIFIER = /^(?:mailing|physical|postal|company|business|office|org|organization|organisation|list|brand|corporate|registered|legal|hq|head|full|footer|mail)$/;
const NOT_SENDER = /^(?:email|e|ip|web|website|shipping|billing|delivery|home|subscriber|contact|customer|recipient|person|user|member|your|my|reply|return)$/;
/**
 * One PART of an address. A footer written as [Sender_Address], [Sender_City],
 * [Sender_State] (SendGrid) or site_settings.company_street_address_1,
 * company_city... (HubSpot) would print the whole address and then the city
 * again, so those are left for the customer and reported instead.
 */
const ADDRESS_PART = /^(?:sender|street|line\d?|\d+|city|state|zip|zipcode|postcode|country|region)$/;
const SINGLE_LINE = /singleline|oneline|addressline|inline/;
/** The Encharge tag a placeholder NAME maps to, if it is the sender's address. */
const addressTagForName = (name) => {
    const words = nameWords(name);
    const compact = words.join("");
    // ActiveCampaign %SENDER-INFO%, Klaviyo {{ organization.full_address }}
    const senderInfo = /^senderinfo/.test(compact);
    const hasAddress = words.some((w) => /^addr(?:ess)?(?:line|html)?$/.test(w)) || /address/.test(compact);
    if (!senderInfo && !hasAddress)
        return undefined;
    if (!senderInfo) {
        if (words.some((w) => NOT_SENDER.test(w) || ADDRESS_PART.test(w)))
            return undefined;
        if (!words.some((w) => ADDRESS_QUALIFIER.test(w)))
            return undefined;
    }
    return SINGLE_LINE.test(compact)
        ? "{{account.mailingAddressSingleLine}}"
        : "{{account.mailingAddress}}";
};
exports.addressTagForName = addressTagForName;
/** A placeholder as it may appear in copy. */
const TEXT_PLACEHOLDER = /\*\|[A-Za-z_][^|<>\s]{0,60}\|\*|\{\{\s*[A-Za-z_][\w.]{0,60}\s*\}\}|%%[A-Za-z_][\w.:-]{0,60}%%|%[A-Za-z_][\w.:-]{0,60}%|\[\[[A-Za-z_][\w .:-]{0,60}\]\]|\[[A-Za-z_][\w .:-]{0,60}\]|\{\$[A-Za-z_][\w.-]{0,60}\}|\{[A-Za-z_][\w.:-]{0,60}\}/g;
/** A text placeholder's closing delimiter. */
const PLACEHOLDER_CLOSE = /(?:\|\*|\}\}|%%|%|\]\]|\]|\})$/;
/** The name inside a text placeholder, e.g. ACME_PHYSICAL_MAILING_ADDRESS. */
const textPlaceholderName = (token) => {
    const opened = token.replace(/^(?:\*\||\{\{|%%|%|\[\[|\[|\{\$|\{)\s*/, "");
    // Drop the closing delimiter, then the blank space before it. One /\s*(?:...)$/ would take
    // time quadratic in a run of blank space, and the lookbehind that kept it linear cannot be
    // used here: the frontend bundles this module, and Safari before 16.4 cannot parse one.
    const close = PLACEHOLDER_CLOSE.exec(opened);
    const name = close ? opened.slice(0, close.index).trimEnd() : opened;
    return name.replace(/^(?:HTML:)?LIST[:_]/i, "LIST_");
};
/**
 * Replace the sender-address placeholders in the email's copy with Encharge's
 * mailing address tag. Only text is touched: an address never belongs inside
 * an attribute.
 */
const mapMailingAddressPlaceholders = (html) => {
    const seen = new Map();
    const out = (0, exports.mapText)(html, (text) => text.replace(TEXT_PLACEHOLDER, (token) => {
        if (/^\{\{\s*(?:person|account)\./.test(token))
            return token;
        const to = (0, exports.addressTagForName)(textPlaceholderName(token));
        if (!to)
            return token;
        const entry = seen.get(token) || { from: token, to, count: 0 };
        entry.count += 1;
        seen.set(token, entry);
        return to;
    }));
    return { html: out, mapped: Array.from(seen.values()) };
};
exports.mapMailingAddressPlaceholders = mapMailingAddressPlaceholders;
/**
 * Placeholders still in the copy that Encharge will not fill, so a reader
 * sees them as typed. Encharge's own Liquid ({{ }}, {% %}) is not listed:
 * that is Encharge syntax, checked by the merge-tag report. A bracketed word
 * counts only when it is written like a placeholder ([SENDER_CITY],
 * [first_name]), so "[sic]" or "[1]" in copy is left out.
 */
const unfilledTextPlaceholders = (html) => {
    const found = new Set();
    for (const span of (0, exports.markupSpans)(html)) {
        if (span.kind !== "text")
            continue;
        // Bracket tags reach the export with their brackets written as
        // character references (see keepBracketTags in html_to_unlayer).
        const text = html
            .slice(span.start, span.end)
            .replace(/&#(?:91|x5b);/gi, "[")
            .replace(/&#(?:93|x5d);/gi, "]");
        const re = new RegExp(TEXT_PLACEHOLDER.source, "g");
        let m;
        // eslint-disable-next-line no-cond-assign
        while ((m = re.exec(text))) {
            const token = m[0];
            if (/^\{\{/.test(token))
                continue;
            // [[...]] is never ordinary copy, whatever the name inside
            if ((/^\[/.test(token) && !/^\[\[/.test(token)) || /^\{[A-Za-z]/.test(token)) {
                const name = textPlaceholderName(token);
                if (!/^[A-Z][A-Z0-9_:]{2,}$/.test(name) && !/^[a-z]+(?:_[a-z0-9]+)+$/i.test(name))
                    continue;
            }
            if (/^%[^%]/.test(token) && !/^%[A-Z][A-Z0-9_:-]{2,}%$/.test(token))
                continue;
            found.add(token);
            if (found.size >= 20)
                return Array.from(found);
        }
    }
    return Array.from(found);
};
exports.unfilledTextPlaceholders = unfilledTextPlaceholders;
//# sourceMappingURL=email_service_links.js.map